import { East } from '@mui/icons-material'
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    List,
    ListItem,
    ListItemIcon,
    ListSubheader,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material'
import { type APIProductModel } from '@visorpro/client'
import { useEffect } from 'react'
import { type DocumentUpdate } from '../../types'
import { hasDocumentChanges } from '../../util/has-document-changes'

export interface DocumentsDialogProps {
    open: boolean
    documentUpdates: DocumentUpdate[]
    isProcessing: boolean
    matchMode: 'sha512_hash' | 'original_filename'
    onMatchModeChange: (mode: 'sha512_hash' | 'original_filename') => void
    onConfirm: () => Promise<void>
    onClose: () => void
    onBack: () => void
}

export const DocumentsDialog = ({
    open,
    documentUpdates,
    isProcessing,
    matchMode,
    onMatchModeChange,
    onConfirm,
    onClose,
    onBack,
}: DocumentsDialogProps) => {
    const changedDocumentUpdates = documentUpdates.filter(
        (documentUpdate) =>
            documentUpdate.new &&
            hasDocumentChanges(documentUpdate.old, documentUpdate.new),
    )
    const noChangesDocumentUpdates = documentUpdates.filter(
        (documentUpdate) =>
            documentUpdate.new &&
            !hasDocumentChanges(documentUpdate.old, documentUpdate.new),
    )
    const unhandledDocumentUpdates = documentUpdates.filter(
        (documentUpdate) =>
            !documentUpdate.new &&
            documentUpdate.old.title === documentUpdate.old.original_filename,
    )
    const unmatchedDocumentUpdates = documentUpdates.filter(
        (documentUpdate) =>
            !documentUpdate.new &&
            documentUpdate.old.title !== documentUpdate.old.original_filename,
    )

    useEffect(() => {
        console.log(documentUpdates)
    }, [documentUpdates])

    const handleMatchMode = (
        event: React.MouseEvent<HTMLElement>,
        newMatchMode: 'sha512_hash' | 'original_filename',
    ) => {
        if (newMatchMode && onMatchModeChange) {
            onMatchModeChange(newMatchMode);
        }
    };

    return (
        <Dialog open={open} fullWidth maxWidth="md">
            <DialogTitle>Update Documents</DialogTitle>
            <DialogContent sx={{ px: 0 }}>
                <DialogContentText sx={{ px: 3, mb: 2, flexDirection: 'row', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ToggleButtonGroup
                        value={matchMode}
                        exclusive
                        onChange={handleMatchMode}
                        aria-label="text alignment"
                    >
                        <ToggleButton value="sha512_hash">
                            SHA512 Hash
                        </ToggleButton>
                        <Tooltip title="It is recommended to use SHA512 Hash for better accuracy, but you can switch to Original Filename if the hashes are not available.
                            To matching using the Original Filename, all the alphanumeric characters have to match exactly, ignoring case and file extension.
                            For example, 'Document-123.pdf' and 'document_123.PDF' would be considered a match because they both have 'document123' as the alphanumeric characters when ignoring case and file extension, 
                            while 'Document-123.pdf' and 'Document-124.pdf' would not be a match.
                        ">
                            <ToggleButton value="original_filename">
                                Original Filename
                            </ToggleButton>
                        </Tooltip>
                    </ToggleButtonGroup>

                </DialogContentText>
                <List
                    sx={{ p: 0, gap: 0, overflowY: 'auto', maxHeight: '60vh' }}>
                    {documentUpdates.length > 0 ? (
                        <>
                            <DocumentListGroup
                                documentUpdates={changedDocumentUpdates}
                                groupTitle={`Matched (${changedDocumentUpdates.length})`}
                            />
                            <DocumentListGroup
                                documentUpdates={noChangesDocumentUpdates}
                                groupTitle={`No Changes (${noChangesDocumentUpdates.length})`}
                                showNew={false}
                            />
                            <DocumentListGroup
                                documentUpdates={unhandledDocumentUpdates}
                                groupTitle={`Unhandled (${unhandledDocumentUpdates.length})`}
                            />
                            <DocumentListGroup
                                documentUpdates={unmatchedDocumentUpdates}
                                groupTitle={`Unmatched (${unmatchedDocumentUpdates.length})`}
                            />
                        </>
                    ) : (
                        <Typography variant="body1" sx={{ px: 3, py: 3 }}>
                            There is no documents to update.
                        </Typography>
                    )}
                </List>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
                <Button onClick={onBack} disabled={isProcessing}>
                    Back
                </Button>
                <Button
                    color="primary"
                    variant="outlined"
                    onClick={onConfirm}
                    disabled={isProcessing}>
                    Confirm
                </Button>
            </DialogActions>
        </Dialog >
    )
}

const DocumentListGroup = ({
    documentUpdates,
    groupTitle,
    showNew = true,
}: {
    documentUpdates: DocumentUpdate[]
    groupTitle: string
    showNew?: boolean
}) => {
    return (
        <>
            <ListSubheader
                sx={{
                    px: 3,
                    background: (theme) => theme.palette.background.default,
                }}>
                {groupTitle}
            </ListSubheader>
            {documentUpdates.map((documentUpdate) => {
                const oldTitle = documentUpdate.old.title
                const oldOriginalFilename = documentUpdate.original_filename
                const oldSha512Hash = documentUpdate.sha512_hash
                const oldModels =
                    documentUpdate.old.document_to_product_model?.map(
                        (relation) => relation.product_model,
                    ) ?? []
                const oldType = documentUpdate.old.type
                const oldText = (
                    <DocumentListItem
                        title={oldTitle}
                        models={oldModels}
                        original_filename={oldOriginalFilename}
                        sha512_hash={oldSha512Hash}
                        type={oldType}
                    />
                )

                let newText = null
                if (showNew && documentUpdate.new) {
                    const newTitle = documentUpdate.new.title
                    const newOriginalFilename =
                        documentUpdate.new.original_filename
                    const newSha512Hash = documentUpdate.new.sha512_hash
                    const newModels =
                        documentUpdate.new.document_to_product_model?.map(
                            (relation) => relation.product_model,
                        ) ?? []
                    const newType = documentUpdate.new.type
                    newText = (
                        <DocumentListItem
                            title={newTitle}
                            models={newModels}
                            original_filename={newOriginalFilename}
                            sha512_hash={newSha512Hash}
                            type={newType}
                        />
                    )
                }

                return (
                    <ListItem
                        key={documentUpdate.sha512_hash + documentUpdate.original_filename}
                        sx={{ px: 3 }}>
                        {oldText}
                        {newText && (
                            <ListItemIcon sx={{ mx: 5 }}>
                                <East />
                            </ListItemIcon>
                        )}
                        {newText}
                    </ListItem>
                )
            })}
        </>
    )
}

const DocumentListItem = ({
    title,
    models,
    original_filename,
    sha512_hash,
    type,
}: {
    title: string
    models: APIProductModel[]
    original_filename: string
    sha512_hash: string
    type: string
}) => {
    const modelChips = models.map((model) => (
        <Chip key={model.id} size="small" label={model.name} />
    ))

    return (
        <Box sx={{
            width: '100%',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
        }}>
            <Typography
                variant="caption"
                sx={{
                    color: (theme) => theme.palette.text.secondary,
                    fontFamily: 'monospace',
                }}>
                {original_filename}
            </Typography>
            <br />
            <Typography
                variant="caption"
                sx={{
                    color: (theme) => theme.palette.text.secondary,
                    fontFamily: 'monospace',
                }}>
                {sha512_hash}
            </Typography>
            <Typography variant="body1">{title}</Typography>
            <Typography variant="caption">Type: {type}</Typography>
            <Box sx={{ mt: 0.5 }}>
                {modelChips.length === 0 ? '' : modelChips}
            </Box>
        </Box>
    )
}
